import { API_BASE_URL, TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

/** Fired when the API rejects the stored session (expired, suspended, password changed). */
export const SESSION_EXPIRED_EVENT = 'chargeos:session-expired';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers, raw = false } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    // A 401 on a request that carried our token means the session is no longer valid: forget it and let the
    // app send the user back to sign-in (the sign-in call itself reports wrong credentials with 401 too).
    if (res.status === 401 && token && !endpoint.startsWith('/auth/login')) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    if (raw) return payload;
    return payload?.data ?? payload;
  }

  /** Like request(), but returns the full `{ data, meta }` envelope. */
  requestWithMeta(endpoint) {
    return this.request(endpoint, { raw: true });
  }

  // ---- Profile ----
  getProfile() {
    return this.request('/profile');
  }

  updateProfile(body) {
    return this.request('/profile', { method: 'PATCH', body });
  }

  addVehicle(body) {
    return this.request('/profile/vehicles', { method: 'POST', body });
  }

  updateVehicle(id, body) {
    return this.request(`/profile/vehicles/${id}`, { method: 'PUT', body });
  }

  deleteVehicle(id) {
    return this.request(`/profile/vehicles/${id}`, { method: 'DELETE' });
  }

  changePassword(body) {
    return this.request('/profile/password', { method: 'POST', body });
  }

  // ---- Admin: users & permissions ----
  adminListUsers(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null)).toString();
    return this.requestWithMeta(`/users/admin${qs ? `?${qs}` : ''}`);
  }

  adminUserStats() {
    return this.request('/users/admin/stats');
  }

  adminCreateUser(body) {
    return this.request('/users/admin', { method: 'POST', body });
  }

  adminUpdateUser(id, body) {
    return this.request(`/users/admin/${id}`, { method: 'PATCH', body });
  }

  adminPermissions() {
    return this.request('/users/admin/permissions');
  }

  adminSavePermissions(permissions) {
    return this.request('/users/admin/permissions', { method: 'PUT', body: { permissions } });
  }

  // ---- Wallet & payments ----
  getWallet() {
    return this.request('/wallet');
  }

  saveAutoReload(body) {
    return this.request('/wallet/auto-reload', { method: 'PUT', body });
  }

  walletLedger(page = 1, pageSize = 20) {
    return this.requestWithMeta(`/wallet/ledger?page=${page}&pageSize=${pageSize}`);
  }

  createTopUp(body) {
    return this.request('/wallet/top-ups', { method: 'POST', body });
  }

  getTopUp(id) {
    return this.request(`/wallet/top-ups/${id}`);
  }

  getTopUpReceipt(id) {
    return this.request(`/wallet/top-ups/${id}/receipt`);
  }

  simulateTopUpPayment(id) {
    return this.request(`/wallet/top-ups/${id}/simulate-payment`, { method: 'POST' });
  }

  // ---- Charging sessions ----
  getStation(id) {
    return this.request(`/public/stations/${id}`);
  }

  verifyQr(body) {
    return this.request('/qr/verify', { method: 'POST', body });
  }

  startSession(body) {
    return this.request('/sessions', { method: 'POST', body });
  }

  async getActiveSession() {
    // The API answers { data: null } when idle; request() would hand back the envelope in that case.
    const r = await this.request('/sessions/active');
    return r && r.id ? r : null;
  }

  getSession(id) {
    return this.request(`/sessions/${id}`);
  }

  stopSession(id) {
    return this.request(`/sessions/${id}/stop`, { method: 'POST' });
  }

  // ---- Reservations ----
  createReservation(body) {
    return this.request('/reservations', { method: 'POST', body });
  }

  async getActiveReservation() {
    // The API answers { data: null } when none; request() would hand back the envelope in that case.
    const r = await this.request('/reservations/active');
    return r && r.id ? r : null;
  }

  cancelReservation(id) {
    return this.request(`/reservations/${id}/cancel`, { method: 'POST' });
  }

  listStations(qs = '') {
    return this.request(`/public/stations?${qs}`);
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  registerDriver({ fullName, email, phone, password, acceptTerms }) {
    return this.request('/auth/register-driver', {
      method: 'POST',
      body: { fullName, email, phone, password, acceptTerms },
    });
  }

  forgotPassword({ email }) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  resetPassword({ token, password }) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password } });
  }

  verifyEmail({ email, code }) {
    return this.request('/auth/verify-email', { method: 'POST', body: { email, code } });
  }

  resendVerification({ email }) {
    return this.request('/auth/resend-verification', { method: 'POST', body: { email } });
  }

  login({ email, password, rememberMe = false }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password, rememberMe } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Charger models & commissioning ----
  async listChargerModels(qs = '') {
    return this.request(`/charger-models?${qs}`, { raw: true });
  }

  getChargerModelSummary() {
    return this.request('/charger-models/summary');
  }

  createChargerModel(body) {
    return this.request('/charger-models', { method: 'POST', body });
  }

  updateChargerModel(id, body) {
    return this.request(`/charger-models/${id}`, { method: 'PUT', body });
  }

  promoteChargerModel(id) {
    return this.request(`/charger-models/${id}/promote`, { method: 'POST' });
  }

  listCommissioningRuns(id) {
    return this.request(`/charger-models/${id}/commissioning-runs`);
  }

  startCommissioningRun(id, firmwareVersion) {
    return this.request(`/charger-models/${id}/commissioning-runs`, { method: 'POST', body: firmwareVersion ? { firmwareVersion } : {} });
  }

  getCommissioningRun(runId) {
    return this.request(`/charger-models/commissioning-runs/${runId}`);
  }

  async downloadCommissioningReport(runId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/charger-models/commissioning-runs/${runId}/report`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Report download failed (${res.status})`);
    return res.blob();
  }

  // ---- Tariffs ----
  listTariffs(qs = '') {
    return this.request(`/tariffs?${qs}`, { raw: true });
  }

  createTariff(body) {
    return this.request('/tariffs', { method: 'POST', body });
  }

  updateTariff(id, body) {
    return this.request(`/tariffs/${id}`, { method: 'PUT', body });
  }

  getTariffSites(id) {
    return this.request(`/tariffs/${id}/sites`);
  }

  assignTariffSites(id, siteIds) {
    return this.request(`/tariffs/${id}/sites`, { method: 'PUT', body: { siteIds } });
  }

  // ---- Ad-hoc guest charging (anonymous; session calls carry the guest token) ----
  getGuestConnector({ chargerId, connectorIndex, s, n }) {
    const qs = new URLSearchParams({ chargerId, connectorIndex, s, n }).toString();
    return this.request(`/guest/connector?${qs}`);
  }

  createGuestSession(body) {
    return this.request('/guest/sessions', { method: 'POST', body });
  }

  getGuestSession(id, token) {
    return this.request(`/guest/sessions/${id}`, { headers: { 'X-Guest-Token': token } });
  }

  simulateGuestPayment(id, token) {
    return this.request(`/guest/sessions/${id}/simulate-payment`, { method: 'POST', headers: { 'X-Guest-Token': token } });
  }

  stopGuestSession(id, token) {
    return this.request(`/guest/sessions/${id}/stop`, { method: 'POST', headers: { 'X-Guest-Token': token } });
  }

  // ---- NOC fleet monitoring & alarms (noc / admin) ----
  getNocSummary() {
    return this.request('/noc/summary');
  }

  listNocChargers({ page, pageSize, q, status }) {
    const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    return this.requestWithMeta(`/noc/chargers?${p}`);
  }

  getNocMap() {
    return this.request('/noc/map');
  }

  getNocCharger(id) {
    return this.request(`/noc/chargers/${id}`);
  }

  listNocAlarms({ page, pageSize, severity }) {
    const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (severity) p.set('severity', severity);
    return this.requestWithMeta(`/noc/alarms?${p}`);
  }

  acknowledgeNocAlarm(id) {
    return this.request(`/noc/alarms/${id}/acknowledge`, { method: 'POST' });
  }

  listNocCommands(id, { page = 1, pageSize = 10 } = {}) {
    return this.requestWithMeta(`/noc/chargers/${id}/commands?page=${page}&pageSize=${pageSize}`);
  }

  sendNocCommand(id, body) {
    return this.request(`/noc/chargers/${id}/commands`, { method: 'POST', body });
  }

  // ---- Maintenance work orders ----
  getMaintenanceSummary() {
    return this.request('/maintenance/summary');
  }

  listMaintenanceStations() {
    return this.request('/maintenance/stations');
  }

  listMaintenanceTickets({ page, pageSize, q, priority, status }) {
    const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q) p.set('q', q);
    if (priority) p.set('priority', priority);
    if (status) p.set('status', status);
    return this.requestWithMeta(`/maintenance/tickets?${p}`);
  }

  getMaintenanceTicket(id) {
    return this.request(`/maintenance/tickets/${id}`);
  }

  createMaintenanceTicket(body) {
    return this.request('/maintenance/tickets', { method: 'POST', body });
  }

  saveMaintenanceDraft(id, body) {
    return this.request(`/maintenance/tickets/${id}`, { method: 'PATCH', body });
  }

  startMaintenanceTicket(id) {
    return this.request(`/maintenance/tickets/${id}/start`, { method: 'POST' });
  }

  resolveMaintenanceTicket(id, body) {
    return this.request(`/maintenance/tickets/${id}/resolve`, { method: 'POST', body });
  }

  // ---- Driver directory & mini-CRM ----
  getDriverSummary() {
    return this.request('/drivers/summary');
  }

  listDrivers({ page, pageSize, q, status, make, wallet }) {
    const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    if (make) p.set('make', make);
    if (wallet) p.set('wallet', wallet);
    return this.requestWithMeta(`/drivers?${p}`);
  }

  getDriver(id) {
    return this.request(`/drivers/${id}`);
  }

  enrollDriver(body) {
    return this.request('/drivers', { method: 'POST', body });
  }

  toggleDriverRfidLock(id) {
    return this.request(`/drivers/${id}/rfid-lock`, { method: 'POST' });
  }

  adjustDriverWallet(id, body) {
    return this.request(`/drivers/${id}/wallet-adjustments`, { method: 'POST', body });
  }

  // ---- Customer disputes & billing adjustments ----
  listDisputes({ page, pageSize, q, status }) {
    const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    return this.requestWithMeta(`/disputes?${p}`);
  }

  getDispute(id) {
    return this.request(`/disputes/${id}`);
  }

  getDisputeTelemetry(id) {
    return this.request(`/disputes/${id}/telemetry`);
  }

  getDisputeLedger(id) {
    return this.request(`/disputes/${id}/ledger`);
  }

  adjustDispute(id, body) {
    return this.request(`/disputes/${id}/adjustment`, { method: 'POST', body });
  }

  rejectDispute(id, body) {
    return this.request(`/disputes/${id}/reject`, { method: 'POST', body });
  }

  // ---- Financial reconciliation & ledger ----
  financeSummary() {
    return this.request('/finance/summary');
  }

  financeAccounts() {
    return this.request('/finance/accounts');
  }

  financeJournal({ page, pageSize, account, q }) {
    const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (account) p.set('account', account);
    if (q) p.set('q', q);
    return this.requestWithMeta(`/finance/ledger?${p}`);
  }

  async exportFinanceJournal({ account, q } = {}) {
    const p = new URLSearchParams();
    if (account) p.set('account', account);
    if (q) p.set('q', q);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/finance/ledger/export?${p}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res.blob();
  }

  listSettlements({ page = 1, pageSize = 5 } = {}) {
    return this.requestWithMeta(`/finance/settlements?page=${page}&pageSize=${pageSize}`);
  }

  getSettlement(id) {
    return this.request(`/finance/settlements/${id}`);
  }

  importSettlement(body) {
    return this.request('/finance/settlements/import', { method: 'POST', body });
  }

  syncSettlement(body) {
    return this.request('/finance/settlements/sync', { method: 'POST', body });
  }

  signOffSettlement(id, body) {
    return this.request(`/finance/settlements/${id}/sign-off`, { method: 'POST', body });
  }

  listFinanceExceptions({ page = 1, pageSize = 5, status = 'Open' } = {}) {
    return this.requestWithMeta(`/finance/exceptions?page=${page}&pageSize=${pageSize}&status=${status}`);
  }

  resolveFinanceException(id, body) {
    return this.request(`/finance/exceptions/${id}/resolve`, { method: 'POST', body });
  }

  runReconciliation(body) {
    return this.request('/finance/reconciliation/run', { method: 'POST', body });
  }

  verifyLedgerIntegrity() {
    return this.request('/finance/integrity/verify', { method: 'POST' });
  }

  resumePayments(body) {
    return this.request('/finance/integrity/resume', { method: 'POST', body });
  }

  // ---- Executive analytics ----
  executiveSummary(range) {
    return this.request(`/analytics/executive?range=${range}`);
  }

  executiveDemand(range, daytype = 'all') {
    return this.request(`/analytics/executive/demand?range=${range}&daytype=${daytype}`);
  }

  executiveStations({ range, q, city, page, pageSize }) {
    const p = new URLSearchParams({ range, page: String(page), pageSize: String(pageSize) });
    if (q) p.set('q', q);
    if (city && city !== 'ALL') p.set('city', city);
    return this.requestWithMeta(`/analytics/executive/stations?${p}`);
  }

  async exportCdrs({ range, siteId } = {}) {
    const p = new URLSearchParams({ range });
    if (siteId) p.set('siteId', siteId);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/analytics/cdrs/export?${p}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res.blob();
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- Demo / public configuration ----
  publicConfig() {
    return this.request('/public/config');
  }

  // ---- Driver session history & disputes ----
  listMySessions(limit = 20) {
    return this.request(`/sessions?limit=${limit}`);
  }

  openDispute(body) {
    return this.request('/disputes', { method: 'POST', body });
  }

  listReservationNotifications() {
    return this.request('/reservations/notifications');
  }

  // ---- Staff tools ----
  adminAuditLog(qs = '') {
    return this.request(`/users/admin/audit-log${qs}`);
  }

  listNetworkSites() {
    return this.request('/network/sites?pageSize=100');
  }

  signChargerQr(chargerId, connectorIndex) {
    return this.request(`/qr/sign?chargerId=${encodeURIComponent(chargerId)}&connectorIndex=${connectorIndex}`);
  }

  listOnboardingChargers() {
    return this.request('/onboarding/chargers');
  }

  getOnboardingCharger(id) {
    return this.request(`/onboarding/chargers/${id}`);
  }

  listChargerMessages(id, limit = 60) {
    return this.request(`/onboarding/chargers/${id}/messages?limit=${limit}`);
  }

  sendThrottle(id, body) {
    return this.request(`/noc/chargers/${id}/throttle`, { method: 'POST', body });
  }

  listAuditHolds() {
    return this.request('/disputes/audit-holds');
  }

  resolveAuditHold(sessionId, body) {
    return this.request(`/disputes/audit-holds/${sessionId}/resolve`, { method: 'POST', body });
  }
}

export default new ApiService();
